import { Routes, Route, Navigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ScrollToTop } from './components/ScrollToTop';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/Login';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';
import { HomePage } from './pages/Home';
import { DocumentsPage } from './pages/Documents';
import { DocumentPage } from './pages/Document';
import { EntriesPage } from './pages/Entries';
import { ListsPage } from './pages/Lists';
import { SpacesPage } from './pages/Spaces';
import { AlmaPage } from './pages/Alma';
import { Profile } from './pages/Profile';
import { ME_QUERY } from './graphql/queries';

/** Wraps routes that require authentication; redirects to login if not authenticated. */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { data, loading } = useQuery(ME_QUERY);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
      </div>
    );
  }

  if (!data?.me) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Root app component: route definitions and protected layout. */
export default function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <HomePage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/docs"
          element={
            <ProtectedRoute>
              <Layout>
                <DocumentsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/docs/*"
          element={
            <ProtectedRoute>
              <Layout>
                <DocumentPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/entries"
          element={
            <ProtectedRoute>
              <Layout>
                <EntriesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/lists"
          element={
            <ProtectedRoute>
              <Layout>
                <ListsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/spaces"
          element={
            <ProtectedRoute>
              <Layout>
                <SpacesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/alma"
          element={
            <ProtectedRoute>
              <Layout>
                <AlmaPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/alma/:sessionId"
          element={
            <ProtectedRoute>
              <Layout>
                <AlmaPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Legacy routes retired in the wiki pivot — send them home. Old list
            detail links land on the rebuilt read-only Lists view. */}
        <Route path="/lists/:id" element={<Navigate to="/lists" replace />} />
        <Route path="/notes" element={<Navigate to="/" replace />} />
        <Route path="/circles" element={<Navigate to="/" replace />} />
        <Route path="/circles/:id" element={<Navigate to="/" replace />} />
        <Route path="/tasks" element={<Navigate to="/" replace />} />
        <Route path="/families/:familyId/dashboard" element={<Navigate to="/" replace />} />
        <Route path="/families/:familyId/entries" element={<Navigate to="/" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
